import { useState, useMemo, useEffect, useCallback } from 'react';
import { runPairwiseRankingTool } from '../../tools/pairwiseRanking';
import { type AppState, type RankingItem, type MatchupResult } from './types';
import { ItemInput } from './components/ItemInput';
import { ComparisonView } from './components/ComparisonView';
import { ResultsView } from './components/ResultsView';
import { recordWin, generateComparisons, generateTieBreakerPairs, sortByRank } from './utils/ranking';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { PairwiseLive } from '../../components/prioritization/session/liveExperience/liveTools/PairwiseLive';
import { useItemCriteriaValues } from '../../components/prioritization/tools/useItemCriteriaValues';
import { LiveToolComplete } from '../../components/prioritization/session/liveExperience/liveTools/LiveToolComplete';

// Stable fallbacks for Zustand selectors. Inline `?? []` / `?? {}` allocate a new
// reference every snapshot when there is no engine event (standalone tools route),
// which Zustand treats as a change and re-renders forever ("Maximum update depth").

function PairwiseRankingContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, shouldAutoStart, useWorkspaceIds, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';

  // Criteria + values for a card — the shared reader every tool uses.
  const getCriteriaValues = useItemCriteriaValues();

  const [state, setState] = useState<AppState>('input');
  const [itemNames, setItemNames] = useState<string[]>([]);
  const [items, setItems] = useState<RankingItem[]>([]);
  const [comparisons, setComparisons] = useState<Array<[string, string]>>([]);
  const [currentComparisonIndex, setCurrentComparisonIndex] = useState(0);
  const [matchupHistory, setMatchupHistory] = useState<MatchupResult[]>([]);
  const [historyStack, setHistoryStack] = useState<Array<{
    items: RankingItem[];
    matchupHistory: MatchupResult[];
    comparisonIndex: number;
  }>>([]);
  
  // Tie-breaking state
  const [tieBreakingItems, setTieBreakingItems] = useState<RankingItem[]>([]);
  const [tieBreakingComparisons, setTieBreakingComparisons] = useState<Array<[string, string]>>([]);
  const [tieBreakingIndex, setTieBreakingIndex] = useState(0);
  const [tiedItemIds, setTiedItemIds] = useState<string[]>([]);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Ranking comes from the canonical engine adapter
  // (src/tools/pairwiseRanking.ts): the page collects comparison wins; the
  // engine owns win-count ordering and tie-aware ranks.
  const [engineRanks, setEngineRanks] = useState<Map<string, number> | null>(null);
  useEffect(() => {
    if (items.length < 2) {
      setEngineRanks(null);
      return;
    }
    let cancelled = false;
    void runPairwiseRankingTool(
      items.map((item) => ({
        id: item.id,
        label: item.name,
        attributes: {
          wins: item.wins,
          losses: item.losses,
          comparisons: item.comparisons,
        },
      })),
      null,
      { max_items: items.length }
    ).then((scoreSet) => {
      if (cancelled) return;
      setEngineRanks(
        new Map(scoreSet.items.map((s) => [s.item_id as string, s.rank as number]))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [items]);

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newNames = toolItems.map(item => item.name);
    setItemNames(prev => [...prev, ...newNames]);

    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach((item, idx) => {
      const workspaceId = item.sourceId ?? item.id;
      const localId = useWorkspaceIds
        ? workspaceId
        : `item-${itemNames.length + idx}`;
      newSourceIdMap[localId] = workspaceId;
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));

    // Auto-start in app / live session
    if (shouldAutoStart) {
      const newItems: RankingItem[] = toolItems.map((item, idx) => {
        const workspaceId = item.sourceId ?? item.id;
        return {
          id: useWorkspaceIds ? workspaceId : `item-${itemNames.length + idx}`,
          name: item.name,
          wins: 0,
          losses: 0,
          comparisons: 0,
        };
      });
      const allComparisons = generateComparisons(newItems);
      setItems(newItems);
      setComparisons(allComparisons);
      setCurrentComparisonIndex(0);
      setMatchupHistory([]);
      setHistoryStack([]);
      setState('comparing');
    }
  }, [shouldAutoStart, useWorkspaceIds, itemNames.length]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by wins (most wins first)
    const sortedItems = [...items].sort((a, b) => b.wins - a.wins);
    
    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      // Tie-aware competition rank from the engine; positional fallback only
      // covers the microtask before the adapter result lands.
      rank: engineRanks?.get(item.id) ?? idx + 1,
      score: item.wins,
      scoreBreakdown: {
        wins: item.wins,
        losses: item.losses,
        comparisons: item.comparisons,
      },
      metadata: {
        wins: item.wins,
        losses: item.losses,
        comparisons: item.comparisons,
      }
    }));
  }, [items, sourceIdMap, engineRanks]);

  // Initialize items when starting comparison
  const handleStart = () => {
    const newItems: RankingItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      wins: 0,
      losses: 0,
      comparisons: 0,
    }));

    const allComparisons = generateComparisons(newItems);
    
    setItems(newItems);
    setComparisons(allComparisons);
    setCurrentComparisonIndex(0);
    setMatchupHistory([]);
    setHistoryStack([]);
    setState('comparing');
  };

  // Save state to history stack before making a change
  const saveState = () => {
    setHistoryStack((prev) => [
      ...prev,
      {
        items: [...items],
        matchupHistory: [...matchupHistory],
        comparisonIndex: currentComparisonIndex,
      },
    ]);
  };

  // Handle comparison selection
  const handleComparison = (winnerId: string) => {
    if (state === 'comparing' && matchupHistory.length >= comparisons.length) {
      return;
    }

    saveState();
    
    const [item1Id, item2Id] = comparisons[currentComparisonIndex];
    
    // Record the win
    const updatedItems = recordWin(items, item1Id, item2Id, winnerId);
    
    // Record matchup history
    const matchup: MatchupResult = {
      pair: [item1Id, item2Id],
      winner: winnerId,
      timestamp: Date.now(),
    };
    
    setItems(updatedItems);
    setMatchupHistory((prev) => [...prev, matchup]);

    // Move to next comparison or finish
    if (currentComparisonIndex < comparisons.length - 1) {
      setCurrentComparisonIndex((prev) => prev + 1);
    } else if (isLiveSession) {
      setCurrentComparisonIndex(comparisons.length);
    } else {
      setState('results');
    }
  };

  // Handle undo
  const handleUndo = () => {
    if (historyStack.length === 0) return;
    
    const previousState = historyStack[historyStack.length - 1];
    setItems(previousState.items);
    setMatchupHistory(previousState.matchupHistory);
    setCurrentComparisonIndex(previousState.comparisonIndex);
    setHistoryStack((prev) => prev.slice(0, -1));
  };

  // Handle tie-breaking
  const handleTieBreak = (itemIds: string[]) => {
    setTiedItemIds(itemIds);
    
    const tiedItems = items.filter((item) => itemIds.includes(item.id));
    
    // Reset wins for tie-breaking round (only compare within tied group)
    const resetItems = tiedItems.map((item) => ({
      ...item,
      wins: 0,
      losses: 0,
      comparisons: 0,
    }));
    
    const tieBreakerComparisons = generateTieBreakerPairs(itemIds);
    
    setTieBreakingItems(resetItems);
    setTieBreakingComparisons(tieBreakerComparisons);
    setTieBreakingIndex(0);
    setState('tie-breaking');
  };

  // Handle tie-breaking comparison
  const handleTieBreakComparison = (winnerId: string) => {
    const [item1Id, item2Id] = tieBreakingComparisons[tieBreakingIndex];
    
    // Record win in tie-breaking items
    const updatedTieItems = recordWin(tieBreakingItems, item1Id, item2Id, winnerId);
    
    setTieBreakingItems(updatedTieItems);

    // Move to next tie-breaking comparison or merge back
    if (tieBreakingIndex < tieBreakingComparisons.length - 1) {
      setTieBreakingIndex((prev) => prev + 1);
    } else {
      // Merge tie-breaking results back into main items
      const mergedItems = items.map((item) => {
        if (tiedItemIds.includes(item.id)) {
          const tieItem = updatedTieItems.find((ti) => ti.id === item.id);
          if (tieItem) {
            return {
              ...item,
              wins: item.wins + tieItem.wins,
            };
          }
        }
        return item;
      });
      
      setItems(mergedItems);
      setTiedItemIds([]);
      setTieBreakingItems([]);
      setTieBreakingComparisons([]);
      setTieBreakingIndex(0);
      if (isLiveSession) {
        setState('comparing');
        setCurrentComparisonIndex(comparisons.length);
      } else {
        setState('results');
      }
    }
  };

  // Reset to start over
  const handleReset = () => {
    setState('input');
    setItemNames([]);
    setItems([]);
    setComparisons([]);
    setCurrentComparisonIndex(0);
    setMatchupHistory([]);
    setHistoryStack([]);
    setTieBreakingItems([]);
    setTieBreakingComparisons([]);
    setTieBreakingIndex(0);
    setTiedItemIds([]);
    setSourceIdMap({});
  };

  // Get current comparison items (main or tie-breaking)
  const currentComparison = useMemo(() => {
    if (state === 'tie-breaking') {
      if (tieBreakingComparisons.length === 0 || tieBreakingIndex >= tieBreakingComparisons.length) {
        return null;
      }
      const [item1Id, item2Id] = tieBreakingComparisons[tieBreakingIndex];
      const item1 = tieBreakingItems.find((i) => i.id === item1Id);
      const item2 = tieBreakingItems.find((i) => i.id === item2Id);
      return item1 && item2 ? { item1, item2 } : null;
    } else {
      if (comparisons.length === 0 || currentComparisonIndex >= comparisons.length) {
        return null;
      }
      const [item1Id, item2Id] = comparisons[currentComparisonIndex];
      const item1 = items.find((i) => i.id === item1Id);
      const item2 = items.find((i) => i.id === item2Id);
      return item1 && item2 ? { item1, item2 } : null;
    }
  }, [state, comparisons, currentComparisonIndex, items, tieBreakingComparisons, tieBreakingIndex, tieBreakingItems]);

  const progress = useMemo(() => {
    if (state === 'tie-breaking') {
      return tieBreakingComparisons.length > 0
        ? ((tieBreakingIndex + 1) / tieBreakingComparisons.length) * 100
        : 0;
    } else {
      return comparisons.length > 0
        ? ((currentComparisonIndex + 1) / comparisons.length) * 100
        : 0;
    }
  }, [state, comparisons, currentComparisonIndex, tieBreakingComparisons, tieBreakingIndex]);

  const totalComparisons = state === 'tie-breaking' 
    ? tieBreakingComparisons.length 
    : comparisons.length;
  const currentComparisonNum = state === 'tie-breaking'
    ? tieBreakingIndex + 1
    : currentComparisonIndex + 1;

  const comparisonsComplete =
    state !== 'tie-breaking' &&
    tiedItemIds.length === 0 &&
    (items.length <= 1 ||
      (comparisons.length > 0 && matchupHistory.length === comparisons.length));
  const canSubmitLive = isLiveSession && comparisonsComplete;
  const liveRankPreview = useMemo(
    () =>
      sortByRank(items).map((item, idx) => ({
        id: item.id,
        name: item.name,
        rank: idx + 1,
        detail: `${item.wins} wins`,
      })),
    [items]
  );

  // Focus management for keyboard shortcuts
  useEffect(() => {
    if (state === 'comparing' || state === 'tie-breaking') {
      const element = document.querySelector('[tabindex="0"]') as HTMLElement;
      element?.focus();
    }
  }, [state, currentComparisonIndex, tieBreakingIndex]);

  return (
    <ToolWrapper
      toolName="Pairwise Comparison"
      toolDescription="Compare items two at a time to determine priority"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={items.length || itemNames.length}
      {...props}
    >
      <div className="w-full">
        {isAppMode && state === 'input' && itemNames.length === 0 && (
          <div className="mb-6 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
            <p className="text-sm text-[var(--text-sec)]">
              {`Import items from your ${importSourceLabel} using the button in the header to begin pairwise comparisons.`}
            </p>
          </div>
        )}
        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              {/* Standalone mode - show ItemInput */}
              {!isAppMode && (
                <ItemInput
                  items={itemNames}
                  onItemsChange={setItemNames}
                  onStart={handleStart}
                />
              )}
            </>
          )}

          {isLiveSession && comparisonsComplete && (
            <LiveToolScaffold
              title="Pairwise comparison"
              subtitle="Your ranking is ready to submit"
              progressDone={comparisons.length || items.length}
              progressTotal={comparisons.length || items.length}
            >
              <LiveToolComplete heading="All comparisons complete" items={liveRankPreview} />
            </LiveToolScaffold>
          )}

          {isLiveSession &&
            !comparisonsComplete &&
            (state === 'comparing' || state === 'tie-breaking') &&
            currentComparison && (
            <LiveToolScaffold
              title="Pairwise comparison"
              subtitle="Pick the more important option each round"
              progressDone={matchupHistory.length}
              progressTotal={comparisons.length}
            >
              <PairwiseLive
                item1={{ ...currentComparison.item1, criteriaValues: getCriteriaValues(currentComparison.item1.id) }}
                item2={{ ...currentComparison.item2, criteriaValues: getCriteriaValues(currentComparison.item2.id) }}
                onSelect={state === 'tie-breaking' ? handleTieBreakComparison : handleComparison}
                onUndo={handleUndo}
                canUndo={historyStack.length > 0 && state !== 'tie-breaking'}
                current={currentComparisonNum}
                total={totalComparisons}
              />
            </LiveToolScaffold>
          )}

          {(state === 'comparing' || state === 'tie-breaking') && currentComparison && !isLiveSession && (
            <ComparisonView
              item1={currentComparison.item1}
              item2={currentComparison.item2}
              onSelect={state === 'tie-breaking' ? handleTieBreakComparison : handleComparison}
              onUndo={handleUndo}
              progress={progress}
              totalComparisons={totalComparisons}
              currentComparison={currentComparisonNum}
              canUndo={historyStack.length > 0 && state !== 'tie-breaking'}
              item1CriteriaValues={getCriteriaValues(currentComparison.item1.id)}
              item2CriteriaValues={getCriteriaValues(currentComparison.item2.id)}
            />
          )}

          {state === 'results' && (
            <ResultsView 
              items={items} 
              matchupHistory={matchupHistory}
              onReset={handleReset}
              onTieBreak={handleTieBreak}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function PairwiseRanking(props: ToolHostProps) {
  return <PairwiseRankingContent {...props} />;
}
